import ReactMarkdown from 'react-markdown'
import type { INotebookContent, ICell, ICodeCell, IMarkdownCell } from '@jupyterlab/nbformat'
import { NotebookOutput } from '../NotebookShared/NotebookOutput'
import type { NbOutput } from '../NotebookShared/nbformat'
import { joinMultiline } from '../NotebookShared/outputUtils'
import './PythonNotebook.css'

interface ReadOnlyPythonNotebookProps {
  notebook: INotebookContent | undefined
}

function CodeCellView({ cell }: { cell: ICodeCell }) {
  const source = joinMultiline(cell.source)
  const executionCount = cell.execution_count
  const promptLabel = executionCount != null ? `[${executionCount}]` : '[ ]'

  return (
    <div className="jp-Cell jp-CodeCell flex flex-col gap-1 py-2">
      <div className="flex gap-3 items-start">
        <div className="jp-InputPrompt shrink-0 select-none">In {promptLabel}:</div>
        <pre className="jp-InputArea-editor flex-1 min-w-0 overflow-x-auto bg-card border border-border rounded text-sm font-mono px-3 py-2 whitespace-pre">
          {source}
        </pre>
      </div>
      {cell.outputs && cell.outputs.length > 0 && (
        <div className="flex gap-3 items-start">
          <div className="jp-OutputPrompt shrink-0 select-none">Out {promptLabel}:</div>
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            {cell.outputs.map((output, i) => (
              <NotebookOutput key={i} output={output as NbOutput} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function MarkdownCellView({ cell }: { cell: IMarkdownCell }) {
  return (
    <div className="jp-Cell jp-MarkdownCell py-2 ps-[68px] pe-2">
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown>{joinMultiline(cell.source)}</ReactMarkdown>
      </div>
    </div>
  )
}

function NotebookStaticView({ nbformat }: { nbformat: INotebookContent }) {
  return (
    <div className="flex flex-col gap-2 p-3">
      {nbformat.cells.map((cell: ICell, i: number) => {
        if (cell.cell_type === 'code') {
          return <CodeCellView key={i} cell={cell as ICodeCell} />
        }
        if (cell.cell_type === 'markdown') {
          return <MarkdownCellView key={i} cell={cell as IMarkdownCell} />
        }
        return null
      })}
    </div>
  )
}

export const ReadOnlyPythonNotebook = ({ notebook }: ReadOnlyPythonNotebookProps) => {
  if (!notebook?.cells) return null

  return (
    <div
      className="notebook-wrapper relative h-full w-full flex flex-col overflow-hidden"
      dir="ltr"
    >
      <div className="flex-1 min-h-0 overflow-auto">
        <NotebookStaticView nbformat={notebook} />
      </div>
    </div>
  )
}
